import React, { useContext, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Podcast, VoicePreset } from "src/types/coaching/podcast";

import _ from "lodash";

import PodcastConfigurations from "../configurations";
import { PodcastDetails } from "../details";

import { MenuState, StudioMenuBar } from "../menu";
import { generateAudioForPodcast, initiatePodcastEditor, invalidatePodcastEditor, invalidateQuickGeneratePodcastState, quickGeneratePodcast, updatePodcast, updateValidationErrorsForPodcast } from "src/redux/actions/coaching/podcast";

import { Asset } from "src/types/assets";
import { preparePodcastConfigurationRequest, preparePodcastRequest, validatePodcastRequest } from "../../edit/helper";
import { Banner } from "src/types/common";

import styles from "./studio.module.scss";
import { PodcastContext } from "../../edit/context";

interface EditStudioProps {
  podcast: Podcast;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuStateChange: (menuState: MenuState) => void;
}

export interface PodcastEditState {
  name: string;
  banner: Banner | null;
  assets: Asset[];
  duration?: number;
  voicePreset?: VoicePreset | null;
  podcastType?: string;
}

export const EditInStudio: React.FC<EditStudioProps> = ({
  podcast,
  menuState,
  onMenuStateChange
}) => {

  const dispatch = useDispatch();

  const podcastContext = useContext(PodcastContext);

  const [editState, setEditState] = useState<PodcastEditState>({
    name: podcast.name || "",
    banner: podcast.banner || null,
    podcastType: podcast.type,
    assets: podcast.assets || [],
    duration: podcast.duration || 0,
    voicePreset: podcast.voicePreset
  });

  const [saving, setSaving] = useState(false);

  const { updated } = useSelector((state: any) => {
    const updating = _.get(
      state.coaching,
      `podcasts.update.['${podcast!.id}'].updating`,
    ) as boolean;
    const updated = _.get(
      state.coaching,
      `podcasts.update.['${podcast!.id}'].updated`,
    ) as boolean;

    return { updating, updated };
  });

  const podcastTranscription = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.item.transcription.item`, null);
  });

  const editedPodcastTranscription = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.editorSession.transcript.item`, null);
  });

  const quickGeneratingPodcast = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.quickGenerate.generating`, false);
  });

  const transcriptGenerated = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.generateTranscript.generated`, null);
  });

  useEffect(() => {
    dispatch(initiatePodcastEditor(podcast.id!));

    setEditState({
      name: podcast.name || "",
      banner: podcast.banner || null,
      assets: podcast.assets || [],
      podcastType: podcast.type,
      duration: podcast.duration || 0,
      voicePreset: podcast.voicePreset
    });

    return () => {
      dispatch(invalidatePodcastEditor(podcast.id!));
    }
  }, []);

  useEffect(() => {
    if (quickGeneratingPodcast && transcriptGenerated && podcast.id) {
      dispatch(invalidateQuickGeneratePodcastState(podcast.id!));
    }
  }, [quickGeneratingPodcast, transcriptGenerated]);

  useEffect(() => {
    if (saving && updated) {
      setSaving(false);
      onMenuStateChange({
        ...menuState,
        secondaryAction: null,
      });
    }
  }, [updated]);

  const handleBackClicked = () => {
    onMenuStateChange({
      ...menuState,
      secondaryAction: null,
    });
  };

  const handleChange = (property, value) => {
    setEditState((prev) => {
      return {
        ...prev,
        [property]: value,
      }
    });
  }

  const hasValidationErrors = (errors) => {
    if (_.size(errors) === 0) {
      return false;
    }

    const properties = ["name", "assets", "duration", "voicePreset"];
    const keys = errors.map(e => {
      return e.property;
    }).filter(e => {
      return _.includes(properties, e);
    });

    if (_.size(keys) === 0) {
      return false;
    }

    return true;
  }

  const handleSaveClicked = () => {
    const updatedPodcast = {
      ...podcast,
      name: editState.name || '',
      banner: editState.banner || undefined,
      type: editState.podcastType,
      assets: editState.assets,
      duration: editState.duration,
      voicePreset: editState.voicePreset!,
    };

    const validationErrors = validatePodcastRequest(updatedPodcast);
    dispatch(
      updateValidationErrorsForPodcast({
        podcast: podcast.id!,
        errors: validationErrors,
      }),
    );

    if (hasValidationErrors(validationErrors)) {
      podcastContext.setShowValidationErrorDialog?.(true);
      return;
    }

    const request = preparePodcastRequest(podcast, updatedPodcast);
    const transcriptsChanged = editedPodcastTranscription && !_.isEqual(podcastTranscription, editedPodcastTranscription);
    if (transcriptsChanged) {
      request.transcripts = editedPodcastTranscription;
    }

    if (_.keys(request).length === 0) {
      onMenuStateChange({
        ...menuState,
        secondaryAction: null,
      });

      return;
    }

    setSaving(true);

    dispatch(
      updatePodcast({
        id: podcast.id!,
        request: request,
      }),
    );
  }

  const handleDiscardClicked = () => {
    onMenuStateChange({
      ...menuState,
      secondaryAction: null,
    });
  }

  const handleGenerateTranscriptClicked = () => {
    const updatedPodcast = {
      ...podcast,
      assets: editState.assets,
      duration: editState.duration,
      type: editState.podcastType,
      voicePreset: editState.voicePreset!,
    };

    const validationErrors = validatePodcastRequest(updatedPodcast);
    dispatch(
      updateValidationErrorsForPodcast({
        podcast: podcast.id!,
        errors: validationErrors,
      }),
    );


    if (hasValidationErrors(validationErrors)) {
      podcastContext.setShowValidationErrorDialog?.(true);
      return;
    }

    const request = preparePodcastConfigurationRequest(podcast, updatedPodcast);
    dispatch(quickGeneratePodcast({
      id: podcast.id!,
      request: request,
      generateAudio: false,
    }));
  }

  const handleGenerateAudioClicked = () => {
    const request = {} as any;
    const transcriptsChanged = editedPodcastTranscription && !_.isEqual(podcastTranscription, editedPodcastTranscription);
    if (transcriptsChanged) {
      request.transcripts = editedPodcastTranscription;
    }

    dispatch(generateAudioForPodcast({
      id: podcast.id!,
      request: request,
    }));
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBackClicked}>
          <i className="paperflite-left-arrow"></i>
          <span>generate a podcast with <q>/studio</q></span>
        </button>
        <div className={styles.menubar}>
          <StudioMenuBar
            podcast={podcast}
            editState={editState}
            menuState={menuState}
            onMenuStateChange={onMenuStateChange}
            onSave={handleSaveClicked}
            onDiscard={handleDiscardClicked}
            onGenerateTranscript={handleGenerateTranscriptClicked}
            onGenerateAudio={handleGenerateAudioClicked}
          />
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.left}>
          <PodcastConfigurations podcast={podcast} value={editState} onChange={handleChange} />
        </div>
        <div className={styles.right}>
          <PodcastDetails podcast={podcast} editState={editState} onChange={handleChange} />
        </div>
      </div>
    </div>
  );
};
